import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, TextField } from "@mui/material";
import { GridColDef, GridRenderCellParams, DataGrid } from "@mui/x-data-grid";
import { useState, useRef, useEffect } from "react";
import { Button, Tab, Modal } from "semantic-ui-react";
import { roleDivisionMapping } from "../../../../features/roleDivision/RoleDivisionMapping";
import { RoleDivisionModalTypes } from "../../../../features/roleDivision/types";
import {
  usePortalTranslation,
  TranslationEnum,
} from "../../../../providers/TranslationProvider";
import {
  ChannelkartNetworkDelete,
  ChannelkartNetworkGet,
} from "../../../../services/networkService";
import { ConfirmationPopUp } from "../../../popup/confirmationPopUp";
import { GenericPopUp } from "../../../popup/genericPopUp";
import DivisionBulkUpload from "../../../roleDivisionComponent/DivisionBulkUpload";
import GenericTree from "../../../roleDivisionComponent/GenericTree";
import RoleDivisionModal from "../../../roleDivisionComponent/RoleDivisionModal";
import { popupType } from "../../../../types/types";
import "./studioRoleDivision.css";

interface StudioRoleDivisionProps {
  handleScreenSwitch: (
    screen: "default" | "logs" | "edit" | "division"
  ) => void;
}

type TreeDataType = {
  name: string;
  children: TreeDataType[];
};

function StudioRoleDivision(props: StudioRoleDivisionProps) {
  const { translate } = usePortalTranslation();
  const CUR_COMPONENT = "Division";
  const CUR_COMPONENT_COMMON = "commonPortal";
  const openPopup = (message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  };
  const defaultModalData: RoleDivisionModalTypes = {
    editDivision: false,
    id: "",
    divisionName: "",
    parent: "admin",
    possibleParents: [],
    permissionGroup: [],
    roleAccessType: "",
    channelHierarchy: false,
    close: () => toggleModal(false),
    openPopup: openPopup,
    refreshPage: refreshPage,
  };
  const [rows, setRows] = useState([]);
  const [allDivisionData, setAllDivisionData] = useState([]);

  const [openModal, toggleModal] = useState(false);

  const [divisionModalData, setDivisionModalData] =
    useState<RoleDivisionModalTypes>(defaultModalData);

  const [genericMessage, setGenericMessage] = useState("");
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [openGenericModal, setOpenGenericModal] = useState(false);
  const [tabValue, setTabValue] = useState<string>("1");
  const [confirmPopupState, setConfirmPopupState] = useState(false);
  const [treeData, setTreeData] = useState<TreeDataType>({
    name: "Root",
    children: [],
  });
  const [popUpMessage, setPopUpMessage] = useState<string>("");

  const popUpSuccessMethod = useRef<() => void>();

  const updateConfirmationPopData = (
    message: string,
    methodHelper: () => void
  ) => {
    popUpSuccessMethod.current = methodHelper;
    setPopUpMessage(message);
    setConfirmPopupState(true);
  };

  const openAddModal = () => {
    setDivisionModalData({
      ...defaultModalData,
      possibleParents: divisionModalData.possibleParents,
    });
    toggleModal(true);
  };

  const deleteDivision = async (divisionName: string, parentName: string) => {
    const parentStr = parentName.replace(/ /g, "%20");
    const divisionString = divisionName.replace(/ /g, "%20");
    const url = `/v1/divisions/${divisionString}/${parentStr}`;

    try {
      const deleteRes = await ChannelkartNetworkDelete(url);
      if (deleteRes.status == 200) {
        openPopup("Successfully Deleted", "Success");
      } else if (deleteRes.status == 400) {
        openPopup("error", "Error");
      }
    } catch (err) {
      openPopup("Something Went Wrong!", "Error");
    }
    refreshPage();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const addNodeToTree = (
    tree: TreeDataType,
    parentNodeName: string,
    newNode: string
  ) => {
    for (let i = 0; i < tree.children.length; i++) {
      addNodeToTree(tree.children[i], parentNodeName, newNode);
    }
    if (tree.name === parentNodeName) {
      tree.children.push({
        name: newNode,
        children: [],
      });
      return;
    }
  };

  const getRoleDivisionData = async () => {
    try {
      const response = await ChannelkartNetworkGet(
        "/v1/divisions?sort=level:asc&size=100&source=portal&callType=scheduled"
      );
      setAllDivisionData(response.data.features);
      setRows(response.data.features);
      let parents: string[] = [];
      let tree = {
        name: "Root",
        children: [],
      };
      response.data.features.forEach(
        async (element: { [key: string]: any }) => {
          var divisionName = element.divisionName;
          if (!parents.includes(divisionName)) {
            parents.push(divisionName);
          }

          addNodeToTree(
            tree,
            element.parent == "none" ? "Root" : element.parent,
            element.divisionName
          );
          setTreeData({ ...tree });
        }
      );
      setDivisionModalData({ ...divisionModalData, possibleParents: parents });
    } catch (err) {
      // Sentry.captureException(err);
    }
  };

  function refreshPage() {
    getRoleDivisionData();
  }

  useEffect(() => {
    getRoleDivisionData();
  }, []);

  const columns: GridColDef[] = roleDivisionMapping.columns.map(
    (columnsMetaData) => {
      return {
        disableColumnMenu: true,
        field: columnsMetaData.mappedValue,
        headerName: translate(
          TranslationEnum.division,
          columnsMetaData.headerName.toUpperCase()
        ),
        sortable: false,
        minWidth: 100,
        maxWidth: 200,
        flex: 1,
        headerAlign: "left",
        align: "left",
      };
    }
  );
  columns.push({
    field: "Edit",
    headerName: translate(TranslationEnum.common_portal, "EDIT").toUpperCase(),
    sortable: false,
    minWidth: 200,
    flex: 1,
    headerAlign: "left",
    align: "left",
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <>
          <div
            className="edit-button"
            onClick={() => {
              setDivisionModalData({
                ...divisionModalData,
                editDivision: true,
                id: params.row.id,
                divisionName: params.row.divisionName,
                parent: params.row.parent,
                permissionGroup: params.row.permissionGroups,
                roleAccessType: params.row.roleAccessType,
                channelHierarchy: params.row.channelDivision,
              });
              toggleModal(true);
            }}
          >
            <Button
              className="studio_division_edit_button"
              variant="contained"
            
              onClick={() => {}}
            >
              <div className="edit-buton-text" style={{color:"white"}}>
              {translate(TranslationEnum.common_portal, "EDIT")}
              </div>
            </Button>
          </div>
          <div
            className="delete-button"
            onClick={() => {
              updateConfirmationPopData(
                "Are you sure want to delete the record?",
                () => {
                  deleteDivision(params.row.divisionName, params.row.parent);
                }
              );
            }}
          >
            <Button
              className="studio_division_delete_button"
              variant="outlined"
              onClick={() => {}}
            >
              {translate(TranslationEnum.common_portal, "DELETE")}
            </Button>
          </div>
        </>
      );
    },
  });

  const searchFields = [
    "divisionName",
    "parent",
    "permissionGroups",
    "roleAccessType",
  ];

  return (
    <div className="role_division_container">
      <div className="app-bar">
        <div className="back-button">
          <Button
            onClick={() => props.handleScreenSwitch("default")}
            variant="text"
            sx={{ fontSize: "16px", color: "#434343" }}
          >
            ←
          </Button>{" "}
          <span className="title">{"Manage Divisions"}</span>
        </div>
      </div>
      <Box sx={{ width: "100%" }} className="manage_role_tabs">
        <TabContext value={tabValue}>
          <Box sx={{ borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              aria-label="lab API tabs example"
              variant="fullWidth"
            >
              <Tab
                label={translate(TranslationEnum.division, "Master Data")}
                value="1"
                className="tab_list_manage_role"
              />
              <Tab
                label={translate(TranslationEnum.division, "Bulk Upload")}
                value="2"
                className="tab_list_manage_role"
              />
            </TabList>
          </Box>

          <TabPanel value="1">
            <div className="division_tree">
              <GenericTree treeData={treeData} />
            </div>

            <div className="division_grid_title">
              <div className="master_data_heading">
                {translate(TranslationEnum.division, "Master Data")}
              </div>
              <Button variant="contained" onClick={openAddModal}>
                {translate(TranslationEnum.common_portal, "Add")}
              </Button>
              <div className="division_search_field">
                <TextField
                  id="outlined-basic"
                  label={translate(TranslationEnum.common_portal, "Search")}
                  variant="outlined"
                  size="small"
                  // fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const searchedData = e.target.value
                      .toString()
                      .toLowerCase();
                    const finalData = allDivisionData.filter((eachRow) => {
                      const allValues = searchFields.map((field) => {
                        return eachRow[field];
                      });

                      return allValues.find((eachValue: any) => {
                        return eachValue
                          ?.toString()
                          .toLowerCase()
                          .includes(searchedData);
                      });
                    });
                    setRows(finalData);
                  }}
                />
              </div>
            </div>

            <div>
              <div className="division_management_grid">
                <DataGrid
                  disableRowSelectionOnClick={true}
                  sx={{
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                  }}
                  autoHeight
                  rows={rows ? rows : []}
                  columns={columns}
                  hideFooter
                  hideFooterPagination
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel value="2">
            <DivisionBulkUpload openPopup={openPopup} />
          </TabPanel>
        </TabContext>
      </Box>

      <Modal
        style={{ zIndex: 80 }}
        open={openModal}
        onClose={() => {
          toggleModal(false);
        }}
      >
        <RoleDivisionModal params={divisionModalData} />
      </Modal>
      <ConfirmationPopUp
        message={popUpMessage}
        openConfirmModal={confirmPopupState}
        successMethod={popUpSuccessMethod.current}
        setOpenConfirmModal={setConfirmPopupState}
      />
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
    </div>
  );
}

export default StudioRoleDivision;
